import { format } from 'date-fns'

import { addPeriodFixedEndDate } from '../../addPeriodFixedEndDate'
import type { SourcingMortgageState } from './mapToLutherMortgage.fixtures'
import { SourcingBasisRateType, SourcingRateBandType, SourcingRatePeriodBasis } from './mapToLutherMortgage.fixtures'

export const mapRateTiers = (rateTiers: SourcingMortgageState['rateBand']): Maybe<RateTiersType[]> | undefined => {
  const sortedrateTiers = rateTiers && sortSourcingRateTiers([...rateTiers])
  const length = sortedrateTiers?.length

  const res = sortedrateTiers?.map((tier, index) => {
    const period_fixed_length_months = (() => {
      // prioritise end date over lenght months
      if (tier['rateBand.endDate'] || !tier['rateBand.ratePeriod']) {
        return undefined
      }

      switch (tier['rateBand.ratePeriodBasis']) {
        case SourcingRatePeriodBasis.Months:
          return tier['rateBand.ratePeriod']
        case SourcingRatePeriodBasis.Years:
          return tier['rateBand.ratePeriod'] * 12
        default:
          return null
      }
    })()

    return {
      rate: tier['rateBand.band'] && Math.round(tier['rateBand.band'] * 100000),
      rate_type: mapRateBand(tier['rateBand.type']),
      period_fixed_end_date: tier['rateBand.endDate'] && format(new Date(tier['rateBand.endDate']), 'yyyy-MM-dd'),
      period_fixed_length_months: period_fixed_length_months || null,
      rate_tracked: mapRateTracking(tier['rateBand.basisRateType']),
      // the last item in the list will always be until end of term
      // relying on SourcingRatePeriodBasis.Term is not possible, since it's not always set
      until_end_of_term: index + 1 === length,
    }
  })

  return addPeriodFixedEndDate(res)
}

const sortSourcingRateTiers = (rateTiers: SourcingMortgageState['rateBand']) =>
  rateTiers?.sort((a, b) => {
    if (a['rateBand.index'] == null || b['rateBand.index'] == null) {
      return 0
    }

    if (a['rateBand.index'] < b['rateBand.index']) {
      return -1
    }

    if (a['rateBand.index'] > b['rateBand.index']) {
      return 1
    }

    return 0
  })

const mapRateTracking = (rateType?: SourcingBasisRateType): RateTracker => {
  const dict: { [key in SourcingBasisRateType]: RateTracker } = {
    [SourcingBasisRateType.Base]: 'BOE_BASE_RATE',
    [SourcingBasisRateType.SVR]: 'LENDER_SVR',
    [SourcingBasisRateType.Libor]: 'LIBOR_1M',
    [SourcingBasisRateType.Sonia]: 'SONIA_1M',
  }

  const res = rateType && dict[rateType]
  return res ?? 'LENDER_SVR'
}

const mapRateBand = (rateBandType?: SourcingRateBandType): MortgageClass => {
  const rateBandDict: { [key in SourcingRateBandType]: MortgageClass } = {
    [SourcingRateBandType.ClassVariable]: 'CLASS_VARIABLE',
    [SourcingRateBandType.ClassFixed]: 'CLASS_FIXED',
    [SourcingRateBandType.ClassDiscount]: 'CLASS_DISCOUNT',
    [SourcingRateBandType.ClassCapped]: 'CLASS_CAPPED',
    [SourcingRateBandType.ClassLibor]: 'CLASS_LIBOR',
    [SourcingRateBandType.ClassTracker]: 'CLASS_TRACKER',
    [SourcingRateBandType.ClassStepped]: 'CLASS_STEPPED',
  }

  const res = rateBandType && rateBandDict[rateBandType]
  return res ?? 'CLASS_VARIABLE'
}
